import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ORGANIZATION_STATUSES, type Organization } from "@/contracts";

import { organizationsQuery, reactivateOrganizationMutation } from "@/api/queries";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { SelectField } from "@/components/ui/field";
import { useTranslation } from "react-i18next";
import { ErrorBlock, LoadingBlock, PageHeader } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { DataTable, EmptyRow, Td, Th } from "@/components/ui/table";
import { useApiClient } from "@/lib/api-context";
import { useListFilters } from "@/lib/use-list-filters";
import { EM_DASH } from "@/lib/format";
import { OrganizationCreateDialog } from "./organization-create-dialog";
import { OrganizationSuspendDialog } from "./organization-suspend-dialog";

const STATUS_LABELS = {
  ACTIVE: { labelKey: "activeOrg" as const, tone: "ok" },
  SUSPENDED: { labelKey: "suspendedOrg" as const, tone: "danger" },
} as const;

/** Platform administration: the whole screen is behind SUPER_ADMIN. */
export function OrganizationsPage() {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const client = useApiClient();
  const queryClient = useQueryClient();

  const { value: filterValue, page, setFilter, setPage } = useListFilters();
  const [creating, setCreating] = useState(false);
  const [suspending, setSuspending] = useState<Organization | null>(null);
  const [reactivating, setReactivating] = useState<Organization | null>(null);

  const filters = {
    ...(filterValue("search") ? { search: filterValue("search") } : {}),
    ...(filterValue("status") ? { status: filterValue("status") } : {}),
    page,
  };

  const organizations = useQuery(organizationsQuery(client, filters));

  const reactivate = useMutation({
    ...reactivateOrganizationMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["organizations"] });
      setReactivating(null);
    },
  });

  return (
    <>
      <PageHeader
        title={t("organizationsTitle")}
        description={t("organizationsDescription")}
        action={<Button onClick={() => setCreating(true)}>{t("addOrganization")}</Button>}
      />

      <Card className="mb-4">
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-2">
            <SearchInput
              value={filterValue("search")}
              placeholder={t("searchOrganizations")}
              onChange={(value) => setFilter("search", value)}
            />

            <SelectField
              label={tCommon("status")}
              value={filterValue("status")}
              onChange={(value) => setFilter("status", value)}
              data={[
                { value: "", label: tCommon("all") },
                ...ORGANIZATION_STATUSES.map((status) => ({
                  value: status,
                  label: t(STATUS_LABELS[status].labelKey),
                })),
              ]}
            />
          </div>
        </CardBody>
      </Card>

      {organizations.isPending ? <LoadingBlock label={t("loadingOrganizations")} /> : null}

      {organizations.isError ? (
        <ErrorBlock error={organizations.error} onRetry={() => void organizations.refetch()} />
      ) : null}

      {organizations.data ? (
        <Card>
          <DataTable caption="Organizations">
            <thead>
              <tr>
                <Th>{tCommon("name")}</Th>
                <Th>{t("colCuit")}</Th>
                <Th>{tCommon("status")}</Th>
                <Th>{t("colTimeZone")}</Th>
                <Th numeric>{t("colMembers")}</Th>
                <Th numeric>{tCommon("sites")}</Th>
                <Th numeric>{tCommon("devices")}</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>

            <tbody>
              {organizations.data.data.length === 0 ? (
                <EmptyRow colSpan={8}>{t("emptyOrganizations")}</EmptyRow>
              ) : null}

              {organizations.data.data.map((organization) => (
                <tr key={organization.id}>
                  <Td className="font-medium">{organization.name}</Td>
                  <Td className="font-mono text-xs">{organization.cuit ?? EM_DASH}</Td>
                  <Td>
                    <Badge tone={STATUS_LABELS[organization.status].tone}>
                      {t(STATUS_LABELS[organization.status].labelKey)}
                    </Badge>
                  </Td>
                  <Td className="text-ink-muted">{organization.timezone}</Td>
                  <Td numeric>{organization.counts.users}</Td>
                  <Td numeric>{organization.counts.sites}</Td>
                  <Td numeric>{organization.counts.devices}</Td>
                  <Td className="text-right">
                    {organization.status === "ACTIVE" ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setSuspending(organization)}
                        aria-label={t("suspendNamed", { name: organization.name })}
                      >
                        {t("suspendService")}
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setReactivating(organization)}
                        aria-label={t("reactivateNamed", { name: organization.name })}
                      >
                        {t("reactivate")}
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>

            <tfoot>
              <tr>
                <Td colSpan={8} className="border-b-0">
                  <Pagination
                    page={organizations.data.pagination.page}
                    totalPages={organizations.data.pagination.totalPages}
                    total={organizations.data.pagination.total}
                    noun={{ one: tCommon("noun.organization.one"), other: tCommon("noun.organization.other") }}
                    onChange={setPage}
                  />
                </Td>
              </tr>
            </tfoot>
          </DataTable>
        </Card>
      ) : null}

      <OrganizationCreateDialog open={creating} onClose={() => setCreating(false)} />

      {suspending ? (
        <OrganizationSuspendDialog organization={suspending} onClose={() => setSuspending(null)} />
      ) : null}

      <ConfirmDialog
        open={reactivating !== null}
        title={t("resumeService")}
        confirmLabel={t("reactivate")}
        pending={reactivate.isPending}
        onConfirm={() => {
          if (reactivating) {
            reactivate.mutate(reactivating.id);
          }
        }}
        onCancel={() => setReactivating(null)}
      >
        {reactivating
          ? `${reactivating.name} will be able to sign in again. Their data was never removed.`
          : null}
      </ConfirmDialog>
    </>
  );
}
